import { forwardRef, useCallback, useRef, useState } from 'react';

import { Field } from '@base-ui/react/field';
import { Select as BaseSelect } from '@base-ui/react/select';
import {
  textInputAffixRecipe,
  textInputBoxRecipe,
  textInputHelperRecipe,
  textInputLabelRecipe,
  textInputRootRecipe,
} from 'components/TextInput';
import { useInheritedDarkMode } from 'components/Theme';
import { cn } from 'components/utils';

import {
  selectCaretGlyphRecipe,
  selectCaretRecipe,
  selectItemCheckRecipe,
  selectItemIconRecipe,
  selectItemLabelRecipe,
  selectItemRecipe,
  selectItemTrailingRecipe,
  selectLeadingGlyphRecipe,
  selectListRecipe,
  selectMenuRecipe,
  selectPositionerRecipe,
  selectTriggerRecipe,
  selectValueRecipe,
} from './Select.recipe';
import { SelectCheckMark } from './SelectCheckMark';

import type { SelectProps } from './types';
import type { SelectRootChangeEventDetails } from '@base-ui/react/select';

/**
 * The caret.
 *
 * Drawn inline rather than taken from an icon library, which is the one place this component departs
 * from the "icons are caller slots" rule the rest of the layer follows. It is not a slot: a select
 * without a caret is not a select, and there is nothing for a caller to decide.
 *
 * EXPORTED from the file rather than transcribed, which matters because a hand-drawn chevron is
 * wrong in ways that are hard to see. What was here was a 1.5px two-segment stroke reconstructed
 * from node I498:41227's percentage insets; the reworked cells draw Phosphor's `CaretDown` (node
 * 48:9002) at its native 16x16, which is a single FILLED path with rounded joins built into the
 * outline rather than added by `stroke-linejoin`.
 *
 * `fill` is set as an ATTRIBUTE on the path, not through CSS, and that is load-bearing rather than
 * stylistic: Phosphor's paths carry no fill of their own and a CSS rule beats a presentational
 * attribute, so forcing `fill` through a utility or `sx` leaves the glyph laid out, sized,
 * accessible and completely blank. No type check, snapshot or a11y check catches it.
 *
 * `currentColor`, so the recipes still own the colour — `selectCaretRecipe` sets it.
 */
const CaretDownIcon = () => (
  <svg aria-hidden="true" className={selectCaretGlyphRecipe()} fill="none" focusable="false" viewBox="0 0 16 16">
    <path
      d="M12.9995 4.8999C13.0783 4.8999 13.1572 4.91466 13.23 4.94482C13.3027 4.97499 13.3686 5.02001 13.4243 5.07568C13.4799 5.13135 13.5241 5.19732 13.5542 5.27002C13.5843 5.34276 13.6001 5.42078 13.6001 5.49951C13.6001 5.57834 13.5844 5.65715 13.5542 5.72998C13.524 5.80263 13.4799 5.86869 13.4243 5.92432L8.42432 10.9243C8.36872 10.98 8.30262 11.024 8.22998 11.0542C8.15714 11.0844 8.07836 11.1001 7.99951 11.1001C7.92076 11.1001 7.84277 11.0844 7.77002 11.0542C7.69733 11.0241 7.63133 10.98 7.57568 10.9243L2.57568 5.92432C2.46311 5.81174 2.3999 5.65872 2.3999 5.49951C2.39998 5.34041 2.46318 5.18819 2.57568 5.07568C2.68819 4.96318 2.84041 4.89998 2.99951 4.8999C3.15872 4.8999 3.31174 4.96311 3.42432 5.07568L7.99951 9.65088L12.5757 5.07568C12.6314 5.01994 12.6972 4.97499 12.77 4.94482C12.8427 4.91472 12.9208 4.89994 12.9995 4.8999Z"
      fill="currentColor"
    />
  </svg>
);

/**
 * Design System 2.0 single-select.
 *
 * Ported from the Figma `Single select` set (file 5aSkE0U4TeWbuIZDvccHjc, node 498:41207). Props
 * mirror the Figma variant axes where they are real inputs rather than depictions of state:
 * `position` is the `Corner Radius` axis, while `State` and `Content` are observed from the DOM
 * because Base UI's `Field` already tracks them.
 *
 * Theme is not a prop. Colours resolve from the nearest `.dark` ancestor through the token layer —
 * including for the portalled menu, which gets there via `useInheritedDarkMode`, the same hook
 * `Dialog` uses. See the note on the sentinel below.
 *
 * The closed control is the text input's box, sharing its recipes rather than reproducing its
 * numbers, so a select and a text input stack without a seam. `Select.recipe` explains how, and
 * `FieldGroup` will stack the two together with no changes of its own.
 *
 * Behaviour worth knowing comes free from Base UI and is the main reason this is not hand-rolled:
 * one tab stop, arrow-key navigation, type-to-move-to-option, focus return to the trigger on close
 * however the list was dismissed, `aria-expanded` on the trigger, and a listbox whose selected row
 * is marked. Those are the parts hand-rolled dropdowns routinely get wrong.
 *
 * Two axes of the design are deliberately absent because the file does not draw them: there is no
 * clear/reset affordance and no required marker. `required` still reaches the form.
 */
export const Select = forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      label,
      labelHidden,
      options,
      position,
      leftIcon,
      helperText,
      error,
      disabled,
      menuFooter,
      'data-testid': testId,
      className,
      rootClassName,
      menuClassName,
      open,
      defaultOpen,
      onOpenChange,
      ...rest
    },
    ref
  ) => {
    const hasHelper = Boolean(helperText);
    const hasLeftIcon = Boolean(leftIcon);

    const boxRef = useRef<HTMLDivElement>(null);

    /**
     * Whether the menu is open — tracked only so `useInheritedDarkMode` can re-resolve the colour
     * scheme each time the portal mounts, which is the one thing that hook needs and Base UI does not
     * hand out. `open` wins when the caller controls it, because a controlled parent can open the
     * select without `onOpenChange` ever firing.
     */
    const [uncontrolledOpen, setUncontrolledOpen] = useState(Boolean(defaultOpen));
    const isOpen = open ?? uncontrolledOpen;

    /**
     * The menu is PORTALLED into `<body>`, so it has none of the trigger's ancestors. The token layer
     * resolves colour from position in the DOM (`.dark`, and the `.ds-select` slot class), which a
     * portal escapes: left alone, a dark page would open a light menu.
     *
     * `useInheritedDarkMode` is the layer's answer to that, and `Dialog` already uses it — the
     * sentinel below marks where this select really lives, and the class comes back to go on the
     * portal. Because the portal is an ANCESTOR of the popup, the popup carries only its own
     * `ds-select` slot class and the ordinary `:where(.dark) .ds-select` rule reaches it, exactly as
     * for a select that never portalled.
     *
     * Re-resolved on every open rather than only on mount, because `.dark` can be added to an
     * ancestor after this component mounts — a Storybook toolbar toggle does exactly that, and so
     * does any page that themes a region on a state change.
     *
     * Portalling is kept rather than avoided: rendering the menu in place would put it inside the
     * field's own box, where an `overflow: hidden` ancestor anywhere up the tree could clip it and
     * a stacking context could bury it.
     */
    const { sentinelRef, inheritedDarkClass } = useInheritedDarkMode(isOpen);

    const handleOpenChange = useCallback(
      (nextOpen: boolean, eventDetails: SelectRootChangeEventDetails) => {
        setUncontrolledOpen(nextOpen);
        onOpenChange?.(nextOpen, eventDetails);
      },
      [onOpenChange]
    );

    return (
      // `invalid` is what puts `data-invalid` on the root and `aria-invalid` on the trigger; every red
      // token keys off it, so the visual and the announced state cannot drift apart.
      <Field.Root
        className={cn(textInputRootRecipe(), 'ds-select', rootClassName)}
        /**
         * The one state attribute this component has to write by hand.
         *
         * The reworked `isOpen=Yes` cells take `background/control/active` and
         * `border/control/active` on the BOX, plus `foreground/secondary/active` on the label, and
         * `textInputBoxRecipe` reads all three through a `data-open` on the `group/ti` root. Base UI
         * does put `data-popup-open` on the trigger, but the trigger is a DESCENDANT of the box and
         * no CSS selector reaches upward from it — so the flag has to arrive here instead.
         *
         * `undefined` rather than `false`, because `data-open="false"` is still a present attribute
         * as far as `[data-open]` is concerned and the field would never read as closed.
         */
        data-open={isOpen || undefined}
        data-testid={testId}
        disabled={disabled}
        invalid={error}
      >
        {/**
         * `items` is what lets `Select.Value` render the chosen option's LABEL in the closed control
         * rather than its raw value. It is fed from the same `options` the list renders, so the two
         * cannot disagree.
         */}
        <BaseSelect.Root
          defaultOpen={defaultOpen}
          disabled={disabled}
          items={options}
          open={open}
          onOpenChange={handleOpenChange}
          {...rest}
        >
          {/* `data-part` names the bordered box so a parent can target it and a test can assert on it
              without depending on a styling class. The slot classes sit on the root. */}
          <div ref={boxRef} className={cn(textInputBoxRecipe({ position, hasHelper }), className)} data-part="box">
            {/* The trigger is the FIRST child and pays all the padding itself, so the button fills the
                box and the entire field opens the menu. Reordering these children, or moving the
                padding onto the box, shrinks the hit area to a 20px strip — see `Select.recipe`. */}
            <BaseSelect.Trigger ref={ref} className={selectTriggerRecipe({ hasHelper, hasLeftIcon, labelHidden })}>
              {/* Both glyph cells keep the text input's POSITION and reveal animation and override
                  only the colour — the file paints a select's caret and leading icon one step
                  brighter than the input's affixes. See `Select.recipe`. */}
              {leftIcon && (
                <span className={cn(textInputAffixRecipe({ side: 'left', labelHidden }), selectLeadingGlyphRecipe())}>
                  {leftIcon}
                </span>
              )}

              <span className={selectValueRecipe()}>
                <BaseSelect.Value />
              </span>

              <BaseSelect.Icon className={cn(textInputAffixRecipe({ side: 'right' }), selectCaretRecipe())}>
                <CaretDownIcon />
              </BaseSelect.Icon>
            </BaseSelect.Trigger>

            {/* Hidden rather than absent, exactly as on `TextInputPill`: the trigger still has to be
                named, and a heading sitting above the field is not an accessible name. `sr-only`
                replaces the floating recipe outright rather than joining it, because that recipe
                positions and animates a label this one never shows. */}
            <Field.Label className={labelHidden ? 'sr-only' : textInputLabelRecipe({ hasLeftIcon })}>
              {label}
            </Field.Label>

            {/**
             * Helper text renders inside the border, which is how the design draws the Error cells.
             *
             * `Field.Error` needs `match` to render off a caller-supplied flag — without it, it
             * renders only for Base UI's own constraint validation, which this component does not
             * drive. Either way the text is wired into `aria-describedby`, so it is announced rather
             * than merely visible.
             */}
            {hasHelper && error && (
              <Field.Error className={textInputHelperRecipe()} match>
                {helperText}
              </Field.Error>
            )}
            {hasHelper && !error && (
              <Field.Description className={textInputHelperRecipe()}>{helperText}</Field.Description>
            )}
          </div>

          {/*
            The sentinel. Marks where this select really lives in the React tree so the portal below
            can inherit the ambient colour scheme; `hidden` keeps it out of layout and out of the a11y
            tree. Same shape as `Dialog`'s, which is where this pattern is already proven.
          */}
          <span ref={sentinelRef} hidden />

          <BaseSelect.Portal className={inheritedDarkClass}>
            {/**
             * The menu follows the shared popup geometry: start-aligned, exactly the bordered
             * field's width, and separated from it by 4px. `alignItemWithTrigger` stays off so the
             * selected row never shifts the panel away from that fixed alignment.
             *
             * `anchor` points at the BORDERED BOX rather than the trigger inside it. The trigger is
             * two pixels smaller across both axes, so using it would make `--anchor-width` and the
             * 4px edge gap describe the wrong rectangle.
             */}
            <BaseSelect.Positioner
              align="start"
              alignItemWithTrigger={false}
              anchor={boxRef}
              className={selectPositionerRecipe()}
              side="bottom"
              sideOffset={4}
            >
              {/* `ds-select` is repeated here because the portal escapes the slot class the same way it
                  escapes the theme class. The theme class goes on the PORTAL rather than here, so this
                  element carries one class and the ordinary dark rule matches it. */}
              <BaseSelect.Popup className={cn('ds-select', selectMenuRecipe(), menuClassName)} data-part="menu">
                {/* `Select.List` renders a plain `<div>`, so the 2px gap between rows has to be
                    declared here rather than on the popup — where it would only separate the list
                    from `menuFooter`. See `selectListRecipe`. */}
                <BaseSelect.List className={selectListRecipe()}>
                  {options.map(({ value, label: optionLabel, icon, trailing, disabled: optionDisabled }) => (
                    <BaseSelect.Item key={value} className={selectItemRecipe()} disabled={optionDisabled} value={value}>
                      {icon && <span className={selectItemIconRecipe()}>{icon}</span>}

                      <BaseSelect.ItemText className={selectItemLabelRecipe()}>{optionLabel}</BaseSelect.ItemText>

                      {/* The caller's own trailing cell, INSIDE the row rather than beside it, so it
                          highlights, scrolls and dims with the option it belongs to — see `types.ts`
                          for why this slot exists at all. */}
                      {trailing && (
                        <span className={selectItemTrailingRecipe()} data-part="option-trailing">
                          {trailing}
                        </span>
                      )}

                      {/**
                       * The selected row's tick, LAST in the row — after the caller's trailing cell,
                       * which is the order the cells draw and the order that keeps the mark in one
                       * place whether or not a row carries a badge.
                       *
                       * `Select.ItemIndicator` mounts only while the row is selected, so an
                       * unselected row has no empty 16px box reserving space for it. That is what the
                       * cells draw too: the `isSelected=False` variants have no `Check` child at all
                       * rather than a hidden one, so the label runs to the row's full width.
                       *
                       * `aria-hidden` on the glyph and nothing here announcing itself: Base UI
                       * already puts `aria-selected` on the row, and a second spoken "selected" is
                       * how a listbox starts reading twice.
                       */}
                      <BaseSelect.ItemIndicator className={selectItemCheckRecipe()} data-part="option-check">
                        <SelectCheckMark />
                      </BaseSelect.ItemIndicator>
                    </BaseSelect.Item>
                  ))}
                </BaseSelect.List>

                {/* Outside `List` on purpose — see the `menuFooter` note in `types.ts`. `data-part`
                    names it so a test can find it without depending on a styling class. */}
                {menuFooter && <div data-part="menu-footer">{menuFooter}</div>}
              </BaseSelect.Popup>
            </BaseSelect.Positioner>
          </BaseSelect.Portal>
        </BaseSelect.Root>
      </Field.Root>
    );
  }
);
